import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { parseISO, format } from "date-fns";

import { AboutManagement } from "../../../../components/AboutManagement";
import { biometricData, maintenance, others, restroomAndVet } from "../../data";
import { HeaderTitle } from "../../../../components/HeaderTitle";
import { IManagementProps, RemoveAnimalByManagement } from "../../interfaces";
import {
  getManagementById,
  removeAnimalByManagement,
} from "../../../../services/management";
import { useNotification } from "../../../../hooks/useNotification";
import { useAuth } from "../../../../hooks/useAuth";
import {
  allAnimals,
  getAllBatchs,
  getAllFlocks,
} from "../../../../services/animals";
import {
  IAnimal,
  IBatchOrFlock,
  IResponseBatchOrFlockAxios,
} from "../../../animals/interface";
import { useTranslate } from "../../../../hooks/useTranslate";
import { Stack } from "@mui/material";

const EditManagement = () => {
  const { errorNotification } = useNotification();
  const params = useParams();
  const { propertiesIds } = useAuth();
  const { translate } = useTranslate();

  const [loading, setLoading] = useState(true);
  const [management, setManagement] = useState<IManagementProps>(
    {} as IManagementProps
  );
  const [animals, setAnimals] = useState<IAnimal[]>([]);
  const [batchs, setBatchs] = useState<IBatchOrFlock[]>([]);
  const [flocks, setFlocks] = useState<IBatchOrFlock[]>([]);

  const boot = async (id: string) => {
    setLoading(true);
    const management: IManagementProps = await getManagementById(id);

    const biometricFiltered = biometricData.filter((item) =>
      management.attributes.activity.includes(item.id)
    );
    const maintenanceFiltered = maintenance.filter((item) =>
      management.attributes.activity.includes(item.id)
    );
    const othersFiltered = others.filter((item) =>
      management.attributes.activity.includes(item.id)
    );
    const restroomAndVetFiltered = restroomAndVet.filter((item) =>
      management.attributes.activity.includes(item.id)
    );

    const allOptions = [
      ...biometricFiltered,
      ...maintenanceFiltered,
      ...othersFiltered,
      ...restroomAndVetFiltered,
    ].map((option) => option.label);

    setManagement({
      ...management,
      createdAtFormatted: format(
        parseISO(management.created_at),
        "dd/MM/yyyy ' - ' hh'h'mm"
      ),
      attributes: {
        ...management.attributes,
        activityFormatted: allOptions,
      },
    });
    setLoading(false);
  };

  const loadAllFlocks = async (id: string) => {
    const data = await getAllFlocks(id);

    const flocksFormatted = data.map(
      ({ id, name, animal }: IResponseBatchOrFlockAxios) => ({
        id,
        name,
        count: animal.meta.records,
      })
    );

    setFlocks(flocksFormatted);
  };

  const loadAllBatchs = async (id: string) => {
    const data = await getAllBatchs(id);

    const batchsFormatted = data.map(
      ({ id, name, animal }: IResponseBatchOrFlockAxios) => ({
        id,
        name,
        count: animal.meta.records,
      })
    );

    setBatchs(batchsFormatted);
  };

  const loadAllAnimals = async (id: string) => {
    const animals = await allAnimals(id);
    setAnimals(animals);
  };

  const handleRemoveAnimal = async ({
    idAnimal,
    idManagement,
  }: RemoveAnimalByManagement) => {
    await removeAnimalByManagement({
      idAnimal,
      idManagement,
    });

    await boot(params.id!);
  };

  const ready = useRef(false);

  useEffect(() => {
    try {
      if (!ready.current) {
        ready.current = true;

        if (params.id) {
          boot(params.id);
        }
        if (propertiesIds) {
          loadAllAnimals(propertiesIds[0]);
          loadAllBatchs(propertiesIds[0]);
          loadAllFlocks(propertiesIds[0]);
        }
      }
    } catch {
      errorNotification(translate("global.error_batch_or_flock"));
    }
  }, [errorNotification, params, propertiesIds, translate]);

  if (loading) return <div />;

  return (
    <Stack spacing={3}>
      <HeaderTitle
        title={translate("management.title_edit")}
        showIcon
        url={`/managements/${management.id}`}
      />

      <AboutManagement
        management={management}
        editable
        animals={animals}
        batchs={batchs}
        flocks={flocks}
        removeAnimal={handleRemoveAnimal}
      />
    </Stack>
  );
};

export { EditManagement };
